/**
 * Everything the admin screens read and write.
 *
 * The types are the API's own, from the generated schema, narrowed with `Narrow` where a
 * screen switches over a field's values. They were written by hand until #388, on the
 * argument that a mismatch shows as a rejected save — true of a write, and not of a read,
 * where a renamed field is an `undefined` on the screen that nothing complains about.
 */

import { LONG_TIMEOUT_MS, request, upload, type MapHome, type Narrow, type Schema } from '@taxi/api-client';

/** What happens when a ride cannot be placed with anybody. */
export const noCarsActions = ['TellTheRider', 'EscalateToDispatcher'] as const;

export type NoCarsAction = (typeof noCarsActions)[number];

/**
 * The rungs of the offer ladder, in the order the API names them.
 *
 * Listed here so the form can offer them; the order a firm chooses is what gets stored,
 * and this array is only the menu.
 */
export const dispatchStages = ['Rank', 'Zone', 'ParentZones', 'Radius'] as const;

export type DispatchStage = (typeof dispatchStages)[number];

export type DispatchSettings = Narrow<
  Schema<'DispatchSettings'>,
  { whenNoCarsFound: NoCarsAction; dispatchStages: DispatchStage[] }
>;

/**
 * The bounds the API enforces, repeated here only to stop the form offering nonsense.
 *
 * Not a second source of truth: the server refuses anything outside these whatever the
 * form allows, and a mismatch shows up as a rejected save rather than as a bad setting.
 * They exist so a number input can carry min and max, which is what stops somebody
 * typing 10000 and finding out afterwards.
 */
export const dispatchLimits = {
  dispatchIntervalSeconds: { min: 1, max: 300 },
  offerTimeoutSeconds: { min: 5, max: 300 },
  maxDispatchAttempts: { min: 1, max: 20 },
  searchTimeoutSeconds: { min: 0, max: 3600 },
  retryAfterSeconds: { min: 0, max: 3600 },
  rankFirstOfferSeconds: { min: 5, max: 300 },
  rankRestOfferSeconds: { min: 5, max: 300 },
  rankExitMarginMetres: { min: 0, max: 1000 },
  roadRankingCandidates: { min: 0, max: 25 },
  acceptanceWindowDays: { min: 1, max: 365 },
  acceptancePenaltyMetres: { min: 0, max: 50000 },
  idleBonusMetresPerMinute: { min: 0, max: 5000 },
  maxFairnessAdjustmentMetres: { min: 0, max: 50000 },
  preBookingLeadMinutes: { min: 0, max: 240 },
  preBookingEscalateMinutes: { min: 0, max: 240 },
} as const;

// --- Ride replay -------------------------------------------------------------

/**
 * How long journeys are kept, and what the Journeys report calls a problem. The bounds are
 * `JourneySettingsEndpoints.Limits` on the server, repeated for the number inputs.
 */
export type JourneySettings = Schema<'JourneySettings'>;

export const journeyLimits = {
  retentionDays: { min: 7, max: 1095 },
  inTownSpeedKmh: { min: 20, max: 200 },
  outOfTownSpeedKmh: { min: 20, max: 200 },
  detourPercent: { min: 5, max: 200 },
  detourMinimumMetres: { min: 0, max: 20000 },
  lateMinutes: { min: 1, max: 60 },
  longWaitMinutes: { min: 1, max: 120 },
  longStopMinutes: { min: 1, max: 60 },
} as const satisfies Record<keyof JourneySettings, { min: number; max: number }>;

// --- Telephony ---------------------------------------------------------------

/**
 * Which line an outbound call leaves on: the one the customer rang, or the firm's own order
 * of lines — the first free one, then the next (#328).
 */
export const outboundLineStrategies = ['ReturnPath', 'FirmOrder'] as const;

export type OutboundLineStrategy = (typeof outboundLineStrategies)[number];

/**
 * What happens to an inbound call once a dispatcher has answered it.
 *
 * Classic is what every firm here already runs: the desk answers and that is all. The
 * other two promise the caller a car, and differ only in whether the caller waits on the
 * line for it or is rung back.
 */
export const callFlows = ['Classic', 'HoldAndAnnounce', 'RingBackOnly'] as const;

export type CallFlow = (typeof callFlows)[number];

/** What happens to a caller who waited the whole timeout with no car found. */
export const holdExpiries = ['RingBackWhenCarFound', 'ReturnToDispatchers'] as const;

export type WhenHoldExpires = (typeof holdExpiries)[number];

/**
 * How a caller waits for a car, and what they are told.
 *
 * The same fifteen fields the platform screen edits. Only `callFlow` means anything on
 * its own: the rest are the knobs of the two flows that are not Classic, which is why the
 * panel greys them out rather than hiding them — a firm deciding whether to switch wants
 * to see what they would be deciding about.
 */
export type CallFlowSettings = Narrow<
  Schema<'CallFlowSettings'>,
  { callFlow: CallFlow; whenHoldExpires: WhenHoldExpires }
>;

/**
 * One firm's telephone system.
 *
 * The two passwords are write-only. They arrive as null and are sent as null unless
 * somebody types a new one; the two booleans beside them are how the form knows whether
 * one is stored at all. The API will not return a credential it holds, which is the whole
 * reason the shape is asymmetric.
 */
export type TelephonySettings = Narrow<
  Schema<'TelephonySettings'>,
  // Optional only on a save; every read fills it in.
  { outboundLineStrategy: OutboundLineStrategy; callFlow: CallFlowSettings; outboundLines: string[] }
>;

/** What one door answered: Reachable, Unauthorised, Unreachable or NotConfigured. */
export type DoorResult = Schema<'DoorResult'>;

/** Both doors, reported apart, because they fail apart. */
export type TelephonyTestResult = Schema<'TelephonyTestResult'>;

export const telephonyLimits = {
  pbxRealtimePort: { min: 1, max: 65535 },
  recordingRetentionDays: { min: 1, max: 3650 },
  trunkChannels: { min: 1, max: 200 },
  trunkChannelsKeptFree: { min: 0, max: 200 },
  maxHeldOnSims: { min: 0, max: 16 },
  holdTimeoutSeconds: { min: 30, max: 1800 },
  ringBackAttempts: { min: 0, max: 5 },
  ringBackRetrySeconds: { min: 15, max: 600 },
  announcementRepeats: { min: 1, max: 5 },
} as const;

/**
 * The reminder gap, which is not one range.
 *
 * Zero is off; anything from twenty seconds up is a gap. The values between are refused
 * by the API, because a caller reminded every three seconds cannot hear the hold music at
 * all — so the field offers zero and then jumps.
 */
export const waitingReminderRange = { off: 0, min: 20, max: 300 } as const;

// --- Zones ------------------------------------------------------------------

export const zoneKinds = ['Area', 'Rank'] as const;
export const zoneShapes = ['Circle', 'Polygon'] as const;
export const surchargeTriggers = ['Pickup', 'Destination', 'Either'] as const;

export type ZoneKind = (typeof zoneKinds)[number];
export type ZoneShape = (typeof zoneShapes)[number];
export type SurchargeTrigger = (typeof surchargeTriggers)[number];

export interface ZoneVertex {
  longitude: number;
  latitude: number;
}

export type Zone = Narrow<
  Schema<'ZoneView'>,
  { kind: ZoneKind; shape: ZoneShape; boundary: ZoneVertex[] | null; surchargeAppliesTo: SurchargeTrigger }
>;

/** What a zone form sends. Everything but the identity. */
export type ZoneDraft = Omit<Zone, 'id' | 'depth'>;

/** The deepest a firm may nest. Enforced by the API; repeated so the form can refuse first. */
export const maxZoneDepth = 3;

// --- Address search ---------------------------------------------------------

/** The three levels the desk's address search reaches, narrowest first. There is no fourth. */
export const searchLevels = ['Town', 'Nearby', 'Wider'] as const;

export type SearchLevel = (typeof searchLevels)[number];

/** One level, drawn: a circle or a boundary, as a zone's shape is. */
export type SearchArea = Narrow<
  Schema<'SearchAreaView'>,
  { level: SearchLevel; shape: ZoneShape; boundary: ZoneVertex[] | null }
>;

/** What drawing a level sends. The level is the address, not the body. */
export type SearchAreaDraft = Omit<SearchArea, 'level'>;

/**
 * Who answers the passenger app's address search when the firm's own places do not (#461).
 * The desk and the driver app are not on it: their maps are ours, and Google's terms allow
 * its results only on Google's map.
 */
export const riderSearchSuppliers = ['OwnMaps', 'Google'] as const;

export type RiderSearchSupplier = (typeof riderSearchSuppliers)[number];

/** `googleAvailable` is false when the deployment has no Google key at all. */
export type RiderSearchSettings = Narrow<
  Schema<'RiderSearchSettings'>,
  { supplier: RiderSearchSupplier }
>;

export const riderSearchApi = {
  get: () => request<RiderSearchSettings>('/api/tenant/settings/rider-search'),

  save: (supplier: RiderSearchSupplier) =>
    request<RiderSearchSettings>('/api/tenant/settings/rider-search', {
      method: 'PUT',
      body: { supplier },
    }),
};

// --- Order labels ------------------------------------------------------------

/** Which picture a label shows: none (its words alone), the shipped one, or the firm's. */
export type LabelPicture = 'None' | 'Default' | 'Own';

/**
 * One order label — a vehicle feature as the firm keeps it (#462). `pictureUrl` is relative
 * to the API and already carries its version, so it changes whenever the picture does.
 */
export type FeatureLabel = Narrow<Schema<'FeatureLabel'>, { picture: LabelPicture }>;

export type FeatureLabelRequest = Schema<'FeatureLabelRequest'>;

/** The largest picture the API takes, repeated so the page can say it before a refusal. */
export const labelPictureMaxBytes = 512 * 1024;

const labelsBase = '/api/tenant/fleet/labels';

export const labelsApi = {
  list: () => request<FeatureLabel[]>(labelsBase),

  add: (body: FeatureLabelRequest) =>
    request<FeatureLabel>(labelsBase, { method: 'POST', body }),

  save: (id: string, body: FeatureLabelRequest) =>
    request<FeatureLabel>(`${labelsBase}/${id}`, { method: 'PUT', body }),

  uploadPicture: (id: string, file: File) =>
    upload<FeatureLabel>(`${labelsBase}/${id}/picture`, file, 'file', file.name),

  /** Words alone — or, with `toDefault`, back to the picture shipped for a starter label. */
  removePicture: (id: string, toDefault = false) =>
    request<FeatureLabel>(`${labelsBase}/${id}/picture${toDefault ? '?toDefault=true' : ''}`, {
      method: 'DELETE',
    }),
};

// --- Cancellation reasons ----------------------------------------------------

/** One reason a passenger may give for cancelling (#464). */
export type CancellationReason = Schema<'CancellationReasonView'>;

export type CancellationReasonRequest = Schema<'CancellationReasonRequest'>;

const reasonsBase = '/api/tenant/settings/cancellation-reasons';

export const cancellationReasonsApi = {
  list: () => request<CancellationReason[]>(reasonsBase),

  add: (body: CancellationReasonRequest) =>
    request<CancellationReason>(reasonsBase, { method: 'POST', body }),

  save: (id: string, body: CancellationReasonRequest) =>
    request<CancellationReason>(`${reasonsBase}/${id}`, { method: 'PUT', body }),
};

// --- Tariffs ----------------------------------------------------------------

export type Tariff = Schema<'TariffView'>;

/** A new version of a tariff. Never an edit of an existing one — see the panel. */
export interface TariffDraft {
  code: string;
  displayName: string;
  vehicleCategoryCode: string | null;
  effectiveFrom: string | null;
  baseFare: number;
  perKilometre: number;
  perMinute: number;
  perWaitingMinute: number;
  freeWaitingMinutes: number;
  minimumFare: number;
  currency: string;
  roundingIncrement: number;
}

// --- Night and holiday rules -------------------------------------------------

export const surchargeKinds = ['TimeBand', 'Holiday'] as const;

export type SurchargeKind = (typeof surchargeKinds)[number];

/**
 * What a rule does once it applies: prices of its own in place of the tariff's — how a
 * Bulgarian night tariff is written, every price separately — or an uplift on top of them.
 * Prices first, because that is the one a firm here reaches for.
 */
export const surchargeModes = ['Rates', 'Uplift'] as const;

export type SurchargeMode = (typeof surchargeModes)[number];

/** The prices a rule with prices of its own carries — the tariff's own six. */
export const surchargePrices = [
  'baseFare',
  'perKilometre',
  'perMinute',
  'perWaitingMinute',
  'minimumFare',
  'freeWaitingMinutes',
] as const;

/** Day-of-week flags, matching the API. Sunday is the low bit. */
export const daySet = {
  Sunday: 1,
  Monday: 2,
  Tuesday: 4,
  Wednesday: 8,
  Thursday: 16,
  Friday: 32,
  Saturday: 64,
} as const;

export const everyDay = 127;

export type Surcharge = Schema<'SurchargeView'>;

export type Holiday = Schema<'HolidayView'>;

// --- The client --------------------------------------------------------------

/** What the firm's drivers' phones may do: eight switches and one number. */
export type DriverAppSettings = Schema<'DriverAppSettings'>;

/** The bounds the API enforces on the one number, repeated so the box can carry them. */
export const driverAppLimits = {
  nextJobMinutesBeforeFree: { min: 1, max: 30 },
} as const;

export const settingsApi = {
  driverApp: () => request<DriverAppSettings>('/api/tenant/settings/driver-app'),

  saveDriverApp: (settings: DriverAppSettings) =>
    request<DriverAppSettings>('/api/tenant/settings/driver-app', {
      method: 'PUT',
      body: settings,
    }),

  dispatch: () => request<DispatchSettings>('/api/tenant/settings/dispatch'),

  saveDispatch: (settings: DispatchSettings) =>
    request<DispatchSettings>('/api/tenant/settings/dispatch', {
      method: 'PUT',
      body: settings,
    }),

  journeys: () => request<JourneySettings>('/api/tenant/settings/journeys'),

  saveJourneys: (settings: JourneySettings) =>
    request<JourneySettings>('/api/tenant/settings/journeys', { method: 'PUT', body: settings }),

  telephony: () => request<TelephonySettings>('/api/tenant/settings/telephony'),

  saveTelephony: (settings: TelephonySettings) =>
    request<TelephonySettings>('/api/tenant/settings/telephony', {
      method: 'PUT',
      body: settings,
    }),

  testTelephony: () =>
    request<TelephonyTestResult>('/api/tenant/settings/telephony/test', { method: 'POST' }),

  zones: () => request<Zone[]>('/api/zones'),

  createZone: (zone: ZoneDraft) =>
    request<Zone>('/api/zones', { method: 'POST', body: zone }),

  updateZone: (id: string, zone: ZoneDraft) =>
    request<Zone>(`/api/zones/${id}`, { method: 'PUT', body: zone }),

  deleteZone: (id: string) =>
    request<void>(`/api/zones/${id}`, { method: 'DELETE' }),

  searchAreas: () => request<SearchArea[]>('/api/tenant/settings/address-search'),

  drawSearchArea: (level: SearchLevel, area: SearchAreaDraft) =>
    request<SearchArea>(`/api/tenant/settings/address-search/${level}`, { method: 'PUT', body: area }),

  clearSearchArea: (level: SearchLevel) =>
    request<void>(`/api/tenant/settings/address-search/${level}`, { method: 'DELETE' }),

  tariffs: () => request<Tariff[]>('/api/tariffs'),

  introduceTariff: (tariff: TariffDraft) =>
    request<Tariff>('/api/tariffs', { method: 'POST', body: tariff }),

  surcharges: () => request<Surcharge[]>('/api/tariffs/surcharges'),

  saveSurcharge: (id: string, rule: Omit<Surcharge, 'id'>) =>
    request<Surcharge>(`/api/tariffs/surcharges/${id}`, { method: 'PUT', body: rule }),

  deleteSurcharge: (id: string) =>
    request<void>(`/api/tariffs/surcharges/${id}`, { method: 'DELETE' }),

  holidays: () => request<Holiday[]>('/api/tariffs/holidays'),

  saveHoliday: (id: string, holiday: Omit<Holiday, 'id'>) =>
    request<Holiday>(`/api/tariffs/holidays/${id}`, { method: 'PUT', body: holiday }),

  deleteHoliday: (id: string) =>
    request<void>(`/api/tariffs/holidays/${id}`, { method: 'DELETE' }),

  fees: () => request<FeeSettings>('/api/tenant/settings/fees'),

  saveFees: (settings: FeeSettings) =>
    request<FeeSettings>('/api/tenant/settings/fees', { method: 'PUT', body: settings }),

  payment: () => request<PaymentSettings>('/api/tenant/settings/payment'),

  savePayment: (settings: PaymentSettings) =>
    request<PaymentSettings>('/api/tenant/settings/payment', { method: 'PUT', body: settings }),

  appearance: () => request<AppearanceSettings>('/api/tenant/settings/appearance'),

  saveAppearance: (settings: AppearanceSettings) =>
    request<AppearanceSettings>('/api/tenant/settings/appearance', {
      method: 'PUT',
      body: settings,
    }),

  /** The destructive half. Answers how many overrides it cleared, which is not always any. */
  applyAppearanceToAll: () =>
    request<Schema<'AppliedToAll'>>('/api/tenant/settings/appearance/apply-to-all', {
      method: 'POST',
      body: {},
    }),
};

/**
 * What the firm charges for a ride that never happened.
 *
 * Zero in either box means the firm does not charge that one, which is also where every
 * firm starts. `currency` is the firm's own and read-only here — it is sent back so the
 * boxes can be labelled without a second call.
 */
export type FeeSettings = Schema<'FeeSettings'>;

/** Cash, card or an account — what a ride is recorded as when the driver said nothing. */
export type PaymentMethod = 'Cash' | 'Card' | 'Account';

/**
 * What a completed ride records when nobody said how it was paid.
 *
 * Null means the firm records nothing, and those rides go on counting as unrecorded in the
 * shift book. It reaches rides from the moment it is saved and never touches one that has
 * already ended.
 */
export type PaymentSettings = Narrow<
  Schema<'PaymentSettings'>,
  { defaultPaymentMethod: PaymentMethod | null }
>;

/**
 * Light, dark, or whatever the handset is doing.
 *
 * By name across the wire, matching the backend's `ThemePreference`. There is no string
 * enum converter on that API, so an unrecognised word is refused with the field named rather
 * than silently becoming the first member of the enum.
 */
export type ThemePreference = 'System' | 'Light' | 'Dark';

/**
 * What the firm's drivers' phones look like by default.
 *
 * A default rather than an instruction: a driver with an override of his own keeps it when
 * this changes. Clearing every override is a separate, deliberate action — see
 * `applyAppearanceToAll`.
 */
export type AppearanceSettings = Narrow<
  Schema<'AppearanceSettings'>,
  { driverAppTheme: ThemePreference }
>;

/**
 * A new identifier for a row the server has not seen yet.
 *
 * The save endpoints take the id in the path and upsert, so the client picks it. That
 * makes adding and editing one code path instead of two, and it makes a double-tapped
 * "add" button create one row rather than two.
 *
 * `crypto.randomUUID` exists only in a secure context — https, or localhost — and the
 * dev server on `http://<firm>.taxi.test:5174` is neither, so there every "add" dialog
 * that mints an id failed with "could not be saved" before a request was made. The
 * fallback is the same v4 UUID assembled from `getRandomValues`, which every context has.
 */
export function newId(): string {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const bytes = crypto.getRandomValues(new Uint8Array(16));

  // Version 4, variant 1: the two nibbles the format reserves.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * A firm's own identity, as the admin panel edits it.
 *
 * `logoUrl` is read-only from here: it is set by uploading a logo and cleared by deleting
 * one, so that a firm cannot point their branding at an arbitrary path by editing a field.
 */
export type BrandingSettings = Schema<'BrandingSettings'>;

export type UploadedLogo = Schema<'UploadedLogo'>;

// --- The firm's face: its name, the lines customers ring, a few words ----------------

/** What a line is for. The API stores the name, so the union is the names. */
export const phoneLineKinds = ['Booking', 'Mobile', 'Office', 'WhatsApp', 'Viber'] as const;

export type PhoneLineKind = (typeof phoneLineKinds)[number];

/** One number a customer can ring. */
export interface PhoneLine {
  label: string;
  /** Any national format on the way in; E.164 on the way back. */
  number: string;
  kind: PhoneLineKind;
  /** Whether the passenger app lists it. */
  shownToPassengers: boolean;
  /** The one line both apps dial. At most one; the server flags the first when none is. */
  isSupportLine: boolean;
}

/**
 * The firm as its customers reach it.
 *
 * Beside the branding rather than inside it: the two are written by different screens at
 * different moments, and the profile is what the first screens of a new firm's setup ask
 * for. The name is here and not on the branding because it is the firm's to change, unlike
 * the company code beside it.
 */
export type FirmProfile = Narrow<
  Schema<'FirmProfile'>,
  // Optional only on a save; every read fills it in.
  { phoneLines: PhoneLine[] }
>;

/** The most lines the API accepts, so the form stops offering a row before it is refused. */
export const maxPhoneLines = 10;

export const aboutUsMaxLength = 600;

export const profileApi = {
  get: () => request<FirmProfile>('/api/tenant/settings/profile'),

  save: (profile: FirmProfile) =>
    request<FirmProfile>('/api/tenant/settings/profile', { method: 'PUT', body: profile }),
};

// --- Where the firm's maps open ---------------------------------------------------

/**
 * What the Map page sends: a point and a zoom, or no point at all to clear the choice
 * and let the derivation from the firm's widest zone take over again.
 */
export interface MapHomeChoice {
  longitude: number | null;
  latitude: number | null;
  zoom: number | null;
}

export const mapHomeApi = {
  /** Where the maps open now, and whether the firm chose it — `MapHome` says which. */
  get: () => request<MapHome>('/api/tenant/settings/map'),

  save: (choice: MapHomeChoice) =>
    request<MapHome>('/api/tenant/settings/map', { method: 'PUT', body: choice }),
};

// --- Setup: how far the firm has got ----------------------------------------------

/** Todo, InProgress, Done or NotNeeded, as the API spells them. */
export type SetupItemState = 'Todo' | 'InProgress' | 'Done' | 'NotNeeded';

export type SetupGroup =
  | 'beforeFirstBooking'
  | 'face'
  | 'city'
  | 'money'
  | 'customers'
  | 'withUs';

/**
 * One row of the checklist.
 *
 * Judged by the server from what the firm actually has, never from a tick — a tariff row,
 * a driver holding a licence, an uploaded logo. `arguments` is data for the sentence the
 * row shows about a done item: a name, a date, a count. The words are this app's.
 */
export type SetupItem = Narrow<Schema<'SetupItem'>, { group: SetupGroup; state: SetupItemState }>;

export type SetupGuideStage = 'NotStarted' | 'InProgress' | 'Finished' | 'Dismissed';

export type SetupGuideState = Narrow<Schema<'SetupGuideState'>, { stage: SetupGuideStage }>;

export type SetupState = Narrow<Schema<'SetupState'>, { items: SetupItem[]; guide: SetupGuideState }>;

export const setupApi = {
  /**
   * The checklist, judged now.
   *
   * Refused for anyone but an administrator, which is how the shell and the sidebar learn
   * whether to show the setup at all — by asking, never by reading a role out of the token.
   */
  state: () => request<SetupState>('/api/tenant/setup'),

  /** Takes an item out of the count, or puts it back. Answers with the whole list again. */
  mark: (key: string, notNeeded: boolean) =>
    request<SetupState>(`/api/tenant/setup/steps/${key}`, {
      method: 'PUT',
      body: { notNeeded },
    }),

  /** Records where the guide got to, per firm rather than per browser. */
  guide: (guide: SetupGuideState) =>
    request<SetupGuideState>('/api/tenant/setup/guide', { method: 'PUT', body: guide }),
};

export const brandingApi = {
  get: () => request<BrandingSettings>('/api/tenant/settings/branding'),

  save: (branding: BrandingSettings) =>
    request<BrandingSettings>('/api/tenant/settings/branding', {
      method: 'PUT',
      body: branding,
    }),

  uploadLogo: (file: File) =>
    upload<UploadedLogo>('/api/tenant/settings/branding/logo', file, 'file', file.name),

  deleteLogo: () =>
    request<void>('/api/tenant/settings/branding/logo', { method: 'DELETE' }),
};

/** What one row of an import file could not be read as. */
export type ImportReject = Schema<'ImportReject'>;

/**
 * What an import did, or would do.
 *
 * The same shape either way, because the dry run runs the same code — so what it shows is
 * what would happen rather than an approximation of it.
 */
export type ImportReport = Schema<'ImportReport'>;

/**
 * The formats a customer file can arrive in.
 *
 * Telcom is the system real firms here are switching from, and its export is a binary
 * table rather than anything an office could have edited — so it is offered by name rather
 * than expecting somebody to convert it first.
 */
export const importFormats = ['csv', 'xlsx', 'json', 'telcom'] as const;

export type ImportFormat = (typeof importFormats)[number];

export const importApi = {
  run: (format: ImportFormat, file: File, commit: boolean, areaCode: string | null) => {
    const query = new URLSearchParams({ commit: String(commit) });

    if (areaCode !== null && areaCode.length > 0) query.set('areaCode', areaCode);

    // A firm's whole list is read, saved and linked to its places in one request.
    return upload<ImportReport>(
      `/api/customers/import/${format}?${query.toString()}`,
      file,
      'file',
      file.name,
      LONG_TIMEOUT_MS,
    );
  },
};

// --- The simulation ----------------------------------------------------------
//
// Only ever answered by a development server, and only for a firm the platform has marked
// as a simulated one. Everywhere else this surface does not exist, which is why the panel
// asks for the state once and hides itself when the answer is a refusal.

/** What a simulated firm is doing, and how hard. */
export type SimulationSettings = Schema<'SimulationSettings'>;

/** What one simulated driver is doing. */
export type BotPhase =
  | 'SignedOff'
  | 'Roaming'
  | 'Considering'
  | 'ToPickup'
  | 'WaitingAtPickup'
  | 'OnRide'
  | 'Break'
  | 'Retiring';

export interface SimulationBot {
  driverId: string;
  callSign: string;
  name: string;
  plate: string;
  phase: BotPhase;
  longitude: number;
  latitude: number;
  outsideCity: boolean;
  orderReference: string | null;
}

export type SimulationCounters = Schema<'SimulationCounters'>;

export type SimulationState = Schema<'SimulationStateView'>;

/** Who is calling, for a simulated ring. */
export const callerKinds = ['known', 'unknown', 'withheld'] as const;

export type CallerKind = (typeof callerKinds)[number];

export const simulationApi = {
  state: () => request<SimulationState>('/api/simulation/state'),

  save: (settings: SimulationSettings) =>
    request<SimulationState>('/api/simulation/settings', { method: 'PUT', body: settings }),

  createOrders: (count: number) =>
    request<void>('/api/simulation/orders', { method: 'POST', body: { count } }),

  ring: (kind: CallerKind, answered: boolean) =>
    request<void>('/api/simulation/calls', { method: 'POST', body: { kind, answered } }),

  reset: () => request<void>('/api/simulation/reset', { method: 'POST' }),
};
